import { atom } from 'jotai';
// import { STORAGE_KEYS } from '@/shared/constants';
// 会话相关atoms
// 所有会话的存储atom
export const sessionsAtom = atom([]);
// 会话元数据列表（用于列表显示）
export const sessionMetasAtom = atom((get) => {
    const sessions = get(sessionsAtom);
    return sessions.map((session) => ({
        id: session.id,
        name: session.name,
        scriptName: session.scriptName,
        description: session.description,
        createdAt: session.createdAt,
        updatedAt: session.updatedAt,
        lastPlayedAt: session.lastPlayedAt,
        isActive: session.isActive,
        isCompleted: session.isCompleted,
    }));
});
// 当前活跃会话ID
export const currentSessionIdAtom = atom(null);
// 当前会话详情
export const currentSessionAtom = atom((get) => {
    const sessionId = get(currentSessionIdAtom);
    const sessions = get(sessionsAtom);
    return sessions.find((session) => session.id === sessionId) || null;
});
// 会话搜索和过滤状态
export const sessionSearchAtom = atom('');
export const sessionStatusFilterAtom = atom('all');
export const sessionSortByAtom = atom('lastPlayed');
export const sessionSortOrderAtom = atom('desc');
// 过滤后的会话列表
export const filteredSessionMetasAtom = atom((get) => {
    const sessions = get(sessionMetasAtom);
    const searchQuery = get(sessionSearchAtom);
    const statusFilter = get(sessionStatusFilterAtom);
    const sortBy = get(sessionSortByAtom);
    const sortOrder = get(sessionSortOrderAtom);
    let filtered = sessions;
    // 搜索过滤
    if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter((session) => session.name.toLowerCase().includes(query) ||
            session.scriptName.toLowerCase().includes(query) ||
            (session.description && session.description.toLowerCase().includes(query)));
    }
    // 状态过滤
    switch (statusFilter) {
        case 'active':
            filtered = filtered.filter((session) => session.isActive && !session.isCompleted);
            break;
        case 'completed':
            filtered = filtered.filter((session) => session.isCompleted);
            break;
        case 'paused':
            filtered = filtered.filter((session) => !session.isActive && !session.isCompleted);
            break;
        // 'all' 不需要过滤
    }
    // 排序
    filtered.sort((a, b) => {
        let aValue;
        let bValue;
        switch (sortBy) {
            case 'name':
                aValue = a.name.toLowerCase();
                bValue = b.name.toLowerCase();
                break;
            case 'created':
                aValue = a.createdAt;
                bValue = b.createdAt;
                break;
            case 'updated':
                aValue = a.updatedAt;
                bValue = b.updatedAt;
                break;
            case 'lastPlayed':
                aValue = a.lastPlayedAt;
                bValue = b.lastPlayedAt;
                break;
            default:
                aValue = a.lastPlayedAt;
                bValue = b.lastPlayedAt;
        }
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortOrder === 'asc'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }
        else {
            return sortOrder === 'asc'
                ? aValue - bValue
                : bValue - aValue;
        }
    });
    return filtered;
});
// 最近游戏的会话IDs（按用户存储，由 DataPersistenceProvider 负责持久化）
export const recentSessionIdsAtom = atom([]);
// 最近游戏的会话列表
export const recentSessionsAtom = atom((get) => {
    const recentIds = get(recentSessionIdsAtom);
    const sessions = get(sessionsAtom);
    return recentIds
        .map((id) => sessions.find((session) => session.id === id))
        .filter((session) => session !== undefined)
        .slice(0, 10); // 最多显示10个
});
// 当前会话的消息列表
export const currentSessionMessagesAtom = atom((get) => {
    const session = get(currentSessionAtom);
    return session?.messages || [];
});
// 当前会话的存档点列表
export const currentSessionSavePointsAtom = atom((get) => {
    const session = get(currentSessionAtom);
    return session?.savePoints || [];
});
// 是否正在游戏中（有活跃会话）
export const isInGameAtom = atom((get) => {
    const currentSession = get(currentSessionAtom);
    return currentSession?.isActive || false;
});
// 会话操作相关的写入atoms
export const createSessionAtom = atom(null, (get, set, session) => {
    const sessions = get(sessionsAtom);
    // 设置其他会话为非活跃状态
    const updatedSessions = sessions.map(s => ({ ...s, isActive: false }));
    // 添加新会话（设置为活跃状态）
    const newSession = { ...session, isActive: true };
    set(sessionsAtom, [...updatedSessions, newSession]);
    // 设置为当前会话
    set(currentSessionIdAtom, session.id);
    // 更新最近使用列表
    const recentIds = get(recentSessionIdsAtom);
    const newRecentIds = [session.id, ...recentIds.filter(id => id !== session.id)].slice(0, 10);
    set(recentSessionIdsAtom, newRecentIds);
});
export const updateSessionAtom = atom(null, (get, set, updatedSession) => {
    const sessions = get(sessionsAtom);
    const updatedSessions = sessions.map((session) => session.id === updatedSession.id
        ? { ...updatedSession, updatedAt: Date.now() }
        : session);
    set(sessionsAtom, updatedSessions);
});
export const deleteSessionAtom = atom(null, (get, set, sessionId) => {
    const sessions = get(sessionsAtom);
    const filteredSessions = sessions.filter((session) => session.id !== sessionId);
    set(sessionsAtom, filteredSessions);
    // 从最近使用列表中移除
    const recentIds = get(recentSessionIdsAtom);
    set(recentSessionIdsAtom, recentIds.filter(id => id !== sessionId));
    // 如果当前选中的是被删除的会话，清除选择
    const currentSessionId = get(currentSessionIdAtom);
    if (currentSessionId === sessionId) {
        set(currentSessionIdAtom, null);
    }
});
export const setActiveSessionAtom = atom(null, (get, set, sessionId) => {
    const sessions = get(sessionsAtom);
    // 设置所有会话为非活跃，选中的会话为活跃
    const updatedSessions = sessions.map((session) => ({
        ...session,
        isActive: session.id === sessionId,
        lastPlayedAt: session.id === sessionId ? Date.now() : session.lastPlayedAt,
    }));
    set(sessionsAtom, updatedSessions);
    set(currentSessionIdAtom, sessionId);
    // 更新最近使用列表
    const recentIds = get(recentSessionIdsAtom);
    const newRecentIds = [sessionId, ...recentIds.filter(id => id !== sessionId)].slice(0, 10);
    set(recentSessionIdsAtom, newRecentIds);
});
export const addMessageToCurrentSessionAtom = atom(null, (get, set, message) => {
    const currentSession = get(currentSessionAtom);
    if (!currentSession)
        return;
    const updatedSession = {
        ...currentSession,
        messages: [...currentSession.messages, message],
        stats: {
            ...currentSession.stats,
            totalMessages: currentSession.stats.totalMessages + 1,
        },
        updatedAt: Date.now(),
        lastPlayedAt: Date.now(),
    };
    set(updateSessionAtom, updatedSession);
});
export const addSavePointToCurrentSessionAtom = atom(null, (get, set, savePoint) => {
    const currentSession = get(currentSessionAtom);
    if (!currentSession)
        return;
    const updatedSession = {
        ...currentSession,
        savePoints: [...currentSession.savePoints, savePoint],
        currentSavePointId: savePoint.id,
        updatedAt: Date.now(),
    };
    set(updateSessionAtom, updatedSession);
});
export const loadSavePointAtom = atom(null, (get, set, savePointId) => {
    const currentSession = get(currentSessionAtom);
    if (!currentSession)
        return;
    const savePoint = currentSession.savePoints.find(sp => sp.id === savePointId);
    if (!savePoint)
        return;
    // 恢复到存档点的状态
    const updatedSession = {
        ...currentSession,
        messages: currentSession.messages.slice(0, savePoint.messageIndex),
        gameState: { ...savePoint.gameState },
        currentCharacter: { ...savePoint.character },
        currentSavePointId: savePointId,
        updatedAt: Date.now(),
        lastPlayedAt: Date.now(),
    };
    set(updateSessionAtom, updatedSession);
});
export const updateGameStateAtom = atom(null, (get, set, gameStateUpdate) => {
    const currentSession = get(currentSessionAtom);
    if (!currentSession)
        return;
    const updatedSession = {
        ...currentSession,
        gameState: {
            ...currentSession.gameState,
            ...gameStateUpdate,
        },
        updatedAt: Date.now(),
    };
    set(updateSessionAtom, updatedSession);
});
